'use client';
import React from 'react';
import { Download, User, MapPin, Users } from 'lucide-react';
import { toast } from 'sonner';
import Modal from '@/components/ui/Modal';
import { DeceasedRecord } from './DeceasedRecordsContent';

export default function DeceasedDetailModal({ record: r, onClose }: { record: DeceasedRecord; onClose: () => void }) {
  return (
    <Modal open onClose={onClose} title={r.fullName} subtitle={`Grave ID: ${r.graveId}`} size="xl">
      <div className="space-y-6">
        {/* Sections */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {/* Personal */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <User size={14} className="text-primary" />
              <h3 className="text-sm font-700 text-foreground">Personal Details</h3>
            </div>
            <div className="space-y-2">
              {[
                ['Full Name', r.fullName],
                ['ID Number', r.idNumber],
                ['Date of Birth', r.dateOfBirth],
                ['Gender', r.gender],
                ['Nationality', r.nationality],
                ['Cause of Death', r.causeOfDeath],
              ].map(([label, value]) => (
                <div key={`dd-per-${label}`} className="py-1.5 border-b border-border/50">
                  <p className="text-[11px] text-muted-foreground">{label}</p>
                  <p className={`text-xs font-600 mt-0.5 ${label === 'ID Number' ? 'font-mono-data' : ''}`}>{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Burial */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={14} className="text-primary" />
              <h3 className="text-sm font-700 text-foreground">Burial Details</h3>
            </div>
            <div className="space-y-2">
              {[
                ['Date of Death', r.dateOfDeath],
                ['Burial Date', r.burialDate],
                ['Cemetery', r.cemetery],
                ['Block', r.block],
                ['Plot', r.plot],
                ['Grave ID', r.graveId],
                ['Recorded By', r.recordedBy],
              ].map(([label, value]) => (
                <div key={`dd-bur-${label}`} className="py-1.5 border-b border-border/50">
                  <p className="text-[11px] text-muted-foreground">{label}</p>
                  <p className={`text-xs font-600 mt-0.5 ${label === 'Grave ID' || label === 'Plot' ? 'font-mono-data' : ''}`}>{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Next of Kin */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Users size={14} className="text-primary" />
              <h3 className="text-sm font-700 text-foreground">Next of Kin</h3>
            </div>
            <div className="space-y-2">
              {[
                ['Full Name', r.nextOfKin],
                ['Relationship', r.nokRelationship],
                ['Contact', r.nokContact],
              ].map(([label, value]) => (
                <div key={`dd-nok-${label}`} className="py-1.5 border-b border-border/50">
                  <p className="text-[11px] text-muted-foreground">{label}</p>
                  <p className="text-xs font-600 mt-0.5">{value}</p>
                </div>
              ))}
            </div>

            {/* Documents */}
            <div className="mt-5">
              <h3 className="text-sm font-700 text-foreground mb-3">Documents</h3>
              <div className="space-y-2">
                {[
                  { id: 'ddoc-death', label: 'Death Certificate', status: 'Available' },
                  { id: 'ddoc-permit', label: 'Burial Permit', status: r.burialDate === '—' ? 'Pending' : 'Available' },
                  { id: 'ddoc-id', label: 'ID Copy', status: 'Available' },
                ].map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between py-1.5 border-b border-border/50">
                    <span className="text-xs text-foreground">{doc.label}</span>
                    <span className={`text-[11px] font-600 ${doc.status === 'Available' ? 'text-positive' : 'text-warning'}`}>
                      {doc.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2 border-t border-border">
          <button
            onClick={() => toast.success('Burial certificate downloaded')}
            className="btn-primary text-sm"
          >
            <Download size={14} /> Download Burial Certificate
          </button>
          <button
            onClick={() => toast.success('Record exported')}
            className="btn-secondary text-sm"
          >
            <Download size={14} /> Export Record
          </button>
          <button onClick={onClose} className="btn-ghost ml-auto">Close</button>
        </div>
      </div>
    </Modal>
  );
}